import { GoogleGenerativeAI } from "@google/generative-ai";
import { DishAnalysis, LocationResult, Coordinates } from "../types";

// Initialize Gemini
//const genAI = new GoogleGenerativeAI(import.meta.env.VITE_GEMINI_API_KEY);
const genAI = new GoogleGenerativeAI("AIzaSyAy8cW8PKA5iO-vxlCy_aj7bcLorKfywFw");
/**
 * 1. Analyzes the image using Gemini 1.5 Flash
 */
export const analyzeDish = async (imageFile: File): Promise<DishAnalysis> => {
  try {
    const model = genAI.getGenerativeModel({ model: "gemini-2.5-flash" });
    
    const imageData = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
      reader.readAsDataURL(imageFile);
    });

    const prompt = `Analyze this food image. Provide a JSON response with:
      dishName, description, isHealthy (boolean), 
      nutritionalInfo (calories, protein, carbohydrates, fats),
      ingredients (name, benefit, healthyAlternative, reasonForAlternative),
      healthTips (string array), allergenAlerts (string array).
      Strictly return ONLY JSON.`;

    const result = await model.generateContent([
      prompt,
      { inlineData: { data: imageData, mimeType: imageFile.type } }
    ]);

    const response = await result.response;
const rawText = response.text();

if (!rawText || rawText.trim().length === 0) {
    console.error("AI returned an empty string. Check API quota or model name.");
    throw new Error("Empty response from AI");
}

const cleanText = rawText.replace(/```json|```/g, "").trim();
const data = JSON.parse(cleanText);
    return {
      dishName: data.dishName || "Unknown Dish",
      description: data.description || "",
      isHealthy: !!data.isHealthy,
      ingredients: data.ingredients || [],
      healthTips: data.healthTips || [],
      allergenAlerts: data.allergenAlerts || [],
      nutritionalInfo: data.nutritionalInfo || { calories: "N/A", protein: "N/A", carbohydrates: "N/A", fats: "N/A" }
    };
  } catch (error) {
    console.error("Analysis Error:", error);
    throw new Error("Failed to analyze image.");
  }
};

/**
 * 2. Modern Google Maps Search (Places API New)
 */
export const findNearbyLocations = async (
  query: string,
  location: { latitude: number; longitude: number },
  type: 'restaurant' | 'supermarket'
): Promise<LocationResult> => {
  try {
    const { Place, SearchNearbyRankPreference } = await google.maps.importLibrary("places") as google.maps.PlacesLibrary;
    const center = new google.maps.LatLng(location.latitude, location.longitude);

    const request = {
      fields: ["displayName", "formattedAddress", "location", "id"],
      locationRestriction: { center, radius: 5000 },
      includedPrimaryTypes: [type],
      maxResultCount: 8,
      rankPreference: SearchNearbyRankPreference.POPULARITY,
    };

    // @ts-ignore
    const { places } = await Place.searchNearby(request);
    const safePlaces = places || [];

    const mapChunks = safePlaces.map((p: any) => ({
      title: p.displayName || "Unknown Location",
      uri: `https://www.google.com/maps/place/?q=place_id:${p.id}`,
      location: {
        latitude: p.location.lat(),
        longitude: p.location.lng(),
      }
    }));

    return {
      text: `Found ${mapChunks.length} ${type}s nearby.`,
      mapChunks: mapChunks
    };
  } catch (error) {
    return { text: "Locations unavailable.", mapChunks: [] };
  }
};

/**
 * 3. GET COORDINATES FROM CITY (The missing export causing your build error)
 */
export const getCoordinatesFromCity = async (city: string): Promise<Coordinates> => {
  const geocoder = new google.maps.Geocoder();
  return new Promise((resolve, reject) => {
    geocoder.geocode({ address: city }, (results, status) => {
      if (status === "OK" && results && results[0]) {
        const loc = results[0].geometry.location;
        resolve({ latitude: loc.lat(), longitude: loc.lng() });
      } else {
        reject(new Error("City not found"));
      }
    });
  });
};

/**
 * 4. GENERATE DISH IMAGE (The other missing export)
 */
export const generateDishImage = async (dishName: string, description: string): Promise<string> => {
  // If you don't have an Image Gen API yet, we return a high-quality placeholder 
  // until you integrate DALL-E or similar.
  return `https://source.unsplash.com/800x600/?${encodeURIComponent(dishName + " food")}`;
};